import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ACTIVITY_ICONS = { note: "📝", status: "🔀", call: "📞", viewing: "🏠", email: "✉️", task: "✅" };
const ACTIVITY_TYPES = [
  { key: "note", label: "Note" },
  { key: "call", label: "Call" },
  { key: "viewing", label: "Viewing" },
  { key: "task", label: "Task" },
];

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminLeadDetail() {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(false);

  const [draft, setDraft] = useState({ type: "note", title: "", body: "", outcome: "", scheduled_at: "" });
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailDraft, setEmailDraft] = useState({ template_id: "", subject: "", body: "" });

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/leads/${id}`);
      setLead(data.lead);
      setActivities(data.activities || []);
    } finally { setBusy(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/email-templates").then((r) => setTemplates(r.data.items || [])); }, []);

  const addActivity = async (e) => {
    e.preventDefault();
    if (!draft.body && !draft.title) { toast.error("Add a title or body"); return; }
    await api.post(`/admin/leads/${id}/activities`, draft);
    toast.success("Activity added");
    setDraft({ type: "note", title: "", body: "", outcome: "", scheduled_at: "" });
    load();
  };

  const remove = async (aid) => {
    if (!window.confirm("Remove this activity?")) return;
    await api.delete(`/admin/leads/${id}/activities/${aid}`);
    load();
  };

  const openEmail = (templateId) => {
    const tpl = templates.find((t) => t.id === templateId);
    if (tpl) {
      setEmailDraft({ template_id: tpl.id, subject: tpl.subject, body: tpl.body });
    } else {
      setEmailDraft({ template_id: "", subject: "", body: "" });
    }
    setEmailOpen(true);
  };

  const sendEmail = async () => {
    if (!emailDraft.subject || !emailDraft.body) { toast.error("Subject and body required"); return; }
    try {
      const { data } = await api.post(`/admin/leads/${id}/send-email`, emailDraft);
      if (data.ok === false) {
        toast.error(`Email failed · ${data.error || data.delivery}`);
      } else {
        toast.success(`Email sent · ${data.delivery}`);
        setEmailOpen(false);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send email");
    }
  };

  const inputCls = "w-full border border-[#E5E7EB] px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:border-[#001588]";

  if (busy || !lead) return <div className="p-10 text-gray-500">Loading…</div>;

  return (
    <div className="p-10 max-w-6xl" data-testid="admin-lead-detail">
      <Link to="/admin/leads" className="text-xs uppercase tracking-[0.2em] text-gray-500 hover:text-[#001588]">← Back to leads</Link>

      <div className="mt-3 mb-8 flex items-start justify-between">
        <div>
          <div className="text-xs tracking-[0.24em] uppercase text-gray-500 mb-1">CRM · Lead</div>
          <h1 className="font-serif text-4xl mb-2">{lead.name}</h1>
          <div className="text-sm text-gray-600">
            {lead.email} · {lead.phone || "no phone"} · <span className="uppercase text-xs tracking-wider bg-gray-100 px-2 py-1">{lead.stage || "new"}</span>
          </div>
          {lead.listing_title && <div className="text-sm text-[#001588] mt-1">Interested in {lead.listing_title}</div>}
        </div>
        <div className="text-right text-xs text-gray-500">
          <div>Received {fmtDate(lead.created_at)}</div>
          {lead.updated_at && <div>Updated {fmtDate(lead.updated_at)}</div>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
        {/* Timeline */}
        <div>
          <div className="mb-6 bg-white border border-[#E5E7EB] p-5" data-testid="add-activity-form">
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">Log an activity</div>
            <form onSubmit={addActivity} className="space-y-3">
              <div className="flex gap-2 flex-wrap">
                {ACTIVITY_TYPES.map((a) => (
                  <button
                    type="button" key={a.key}
                    onClick={() => setDraft({ ...draft, type: a.key })}
                    data-testid={`activity-type-${a.key}`}
                    className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${
                      draft.type === a.key ? "bg-[#001588] text-white border-[#001588]" : "bg-white text-gray-700 border-[#E5E7EB]"
                    }`}
                  >{ACTIVITY_ICONS[a.key]} {a.label}</button>
                ))}
              </div>
              {(draft.type === "call" || draft.type === "viewing") && (
                <div className="grid grid-cols-2 gap-2">
                  <input className={inputCls} placeholder={draft.type === "call" ? "Call outcome (connected / voicemail)" : "Viewing outcome"} value={draft.outcome} onChange={(e) => setDraft({ ...draft, outcome: e.target.value })} />
                  {draft.type === "viewing" && (
                    <input type="datetime-local" className={inputCls} value={draft.scheduled_at} onChange={(e) => setDraft({ ...draft, scheduled_at: e.target.value })} />
                  )}
                </div>
              )}
              {(draft.type === "task") && (
                <input type="datetime-local" className={inputCls} value={draft.scheduled_at} onChange={(e) => setDraft({ ...draft, scheduled_at: e.target.value })} />
              )}
              <input className={inputCls} placeholder="Title (optional)" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="activity-title" />
              <textarea rows={3} className={inputCls} placeholder="Notes / details" value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} data-testid="activity-body" />
              <button type="submit" data-testid="activity-submit" className="px-5 py-2 bg-[#001588] text-white text-xs uppercase tracking-[0.2em] hover:bg-black">Add to timeline</button>
            </form>
          </div>

          <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">Activity timeline · {activities.length}</div>
          <div className="space-y-3">
            {activities.map((a) => (
              <div key={a.id} className="bg-white border border-[#E5E7EB] p-4 flex gap-4" data-testid={`activity-${a.id}`}>
                <div className="text-2xl leading-none">{ACTIVITY_ICONS[a.type] || "•"}</div>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-xs uppercase tracking-[0.2em] text-gray-500">{a.type}{a.outcome ? ` · ${a.outcome}` : ""}</div>
                      {a.title && <div className="font-serif text-lg mt-0.5">{a.title}</div>}
                    </div>
                    <div className="text-xs text-gray-500 whitespace-nowrap">{fmtDate(a.created_at)}</div>
                  </div>
                  {a.body && <div className="text-sm text-gray-700 mt-1 whitespace-pre-line">{a.body}</div>}
                  {a.scheduled_at && <div className="text-xs text-gray-500 mt-2">Scheduled: {fmtDate(a.scheduled_at)}</div>}
                  <div className="text-xs text-gray-400 mt-2">by {a.author_name}</div>
                </div>
                <button onClick={() => remove(a.id)} className="text-xs text-red-600 hover:underline self-start" data-testid={`activity-remove-${a.id}`}>Remove</button>
              </div>
            ))}
            {activities.length === 0 && <p className="text-sm text-gray-500">No activity yet.</p>}
          </div>
        </div>

        {/* Right column — send email */}
        <aside className="bg-white border border-[#E5E7EB] p-5 h-fit lg:sticky lg:top-24" data-testid="email-panel">
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">Send email</div>
          <p className="text-xs text-gray-500 mb-3">Pick a template — variables are auto-filled from this lead.</p>
          <div className="space-y-2 mb-3">
            {templates.map((t) => (
              <button
                key={t.id}
                onClick={() => openEmail(t.id)}
                data-testid={`email-template-${t.id}`}
                className="w-full text-left border border-[#E5E7EB] px-3 py-2 hover:border-[#001588] transition-colors"
              >
                <div className="text-xs uppercase tracking-wider text-gray-500">{t.category}</div>
                <div className="text-sm font-medium">{t.name}</div>
              </button>
            ))}
            {templates.length === 0 && <p className="text-xs text-gray-500">No templates. Create some in <Link to="/admin/email-templates" className="text-[#001588] underline">Email templates</Link>.</p>}
          </div>
          <button onClick={() => openEmail("")} className="w-full text-xs uppercase tracking-[0.2em] py-2 border border-[#001588] text-[#001588] hover:bg-[#001588] hover:text-white" data-testid="email-blank">Write from scratch</button>
        </aside>
      </div>

      {emailOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEmailOpen(false)} data-testid="email-modal">
          <div className="bg-white w-full max-w-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Email to {lead.email}</div>
            <h2 className="font-serif text-2xl mb-4">Compose</h2>
            <div className="space-y-3">
              <input className={inputCls} placeholder="Subject" value={emailDraft.subject} onChange={(e) => setEmailDraft({ ...emailDraft, subject: e.target.value })} data-testid="email-subject" />
              <textarea rows={12} className={`${inputCls} font-mono text-sm`} placeholder="Body" value={emailDraft.body} onChange={(e) => setEmailDraft({ ...emailDraft, body: e.target.value })} data-testid="email-body" />
              <p className="text-xs text-gray-500">Variables: {"{{name}}"} {"{{email}}"} {"{{phone}}"} {"{{listing_title}}"} {"{{agent_name}}"}</p>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setEmailOpen(false)} className="px-5 py-2 border border-[#E5E7EB] text-sm">Cancel</button>
                <button onClick={sendEmail} className="px-5 py-2 bg-[#001588] text-white text-sm uppercase tracking-[0.2em]" data-testid="email-send">Send &amp; log</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
